import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../../context/AuthContext";
import { oneKlicks } from "../../../SDK/oneclick";
import { toast } from "react-toastify";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Badge } from "react-bootstrap";
import { Button } from "antd";
import { ArrowLeftOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";

const PolicyDetails = () => {
    const { userData } = useAuth();
    const { id } = useParams();
    const navigate = useNavigate();
    const [policy, setPolicy] = useState(null);
    const [loading, setLoading] = useState(false);

    const companyId = useMemo(
        () => userData?.company_id || userData?.companyId || userData?.company,
        [userData]
    );

    useEffect(() => {
        fetchPolicyDetails();
    }, [id, companyId]);

    const formatCurrency = (amount) => {
        if (!amount) {
            return "₹0";
        }
        const numAmount = parseFloat(amount);
        return new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency: "INR",
            maximumFractionDigits: 0,
        }).format(numAmount);
    };

    const fetchPolicyDetails = async () => {
        try {
            setLoading(true);
            const conditions = { id };
            if (companyId) {
                conditions.company_id = companyId;
            }

            const response = await oneKlicks.read("", "insurance_policies", conditions, "id DESC", "", "", "1");

            if (response?.success && Array.isArray(response?.data) && response.data.length > 0) {
                setPolicy(response.data[0]);
            } else {
                toast.error('Policy not found');
                navigate('/insurance/policies');
            }
        } catch (error) {
            console.error("Error fetching policy:", error);
            toast.error('Failed to load policy details');
            navigate('/insurance/policies');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="subbody" style={{ width: '100%', padding: '2% 4%' }}>
            <div className="dc-haslayout dc-dbsectionspace">
                <div className="container-fluid py-3">
                    {/* Header Section */}
                    <div className="card mb-5" style={{ borderRadius: '5px' }}>
                        <div className="card-body" style={{ padding: '24px 30px', borderRadius: '5px' }}>
                            <div className="d-flex align-items-center justify-content-between mb-4">
                                <div className="d-flex align-items-center gap-2">
                                    <h5 className="mb-0 font-weight-bold">Policy Details</h5>
                                    {policy && (
                                        <>
                                            <Badge bg="info" style={{ borderRadius: '5px' }}>Policy</Badge>
                                            <Badge bg={policy.status === "active" ? "success" : policy.status === "expired" ? "danger" : "warning"} style={{ borderRadius: '5px' }}>
                                                {policy.status || "N/A"}
                                            </Badge>
                                        </>
                                    )}
                                </div>
                                <Button 
                                    className="btn-outline-primary" 
                                    onClick={() => navigate("/insurance/policies")}
                                    style={{ height: '40px', borderRadius: '5px' }}
                                >
                                    <ArrowLeftOutlined /> Back to Policies
                                </Button>
                            </div>
                        </div>
                    </div>

                    {/* Policy Details Section */}
                    {loading ? (
                        <div className="card" style={{ borderRadius: '5px' }}>
                            <div className="card-body" style={{ padding: '24px 30px', borderRadius: '5px' }}>
                                <div className="text-center p-5">
                                    <div className="spinner-border" role="status">
                                        <span className="sr-only">Loading...</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : policy ? (
                        <div className="card" style={{ borderRadius: '5px' }}>
                            <div className="card-body" style={{ padding: '24px 30px', borderRadius: '5px' }}>
                                <div className="row">
                                    <div className="col-12 col-md-6 mb-4">
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={{ display: 'block', fontWeight: '600', color: '#666', marginBottom: '8px', fontSize: '13px', textTransform: 'uppercase' }}>
                                                Policy Number
                                            </label>
                                            <p style={{ margin: 0, fontSize: '16px', color: '#333', fontWeight: '500' }}>
                                                {policy.policy_number || "N/A"}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="col-12 col-md-6 mb-4">
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={{ display: 'block', fontWeight: '600', color: '#666', marginBottom: '8px', fontSize: '13px', textTransform: 'uppercase' }}>
                                                Status
                                            </label>
                                            <div>
                                                <span className={`badge ${policy.status === "active" ? "badge-success" : policy.status === "expired" ? "badge-danger" : "badge-warning"}`} style={{ borderRadius: '5px' }}>
                                                    {policy.status || "N/A"}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12 col-md-6 mb-4">
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={{ display: 'block', fontWeight: '600', color: '#666', marginBottom: '8px', fontSize: '13px', textTransform: 'uppercase' }}>
                                                Policy Holder Name
                                            </label>
                                            <p style={{ margin: 0, fontSize: '16px', color: '#333', fontWeight: '500' }}>
                                                {policy.policy_holder_name || "N/A"}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="col-12 col-md-6 mb-4">
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={{ display: 'block', fontWeight: '600', color: '#666', marginBottom: '8px', fontSize: '13px', textTransform: 'uppercase' }}>
                                                Coverage Type
                                            </label>
                                            <p style={{ margin: 0, fontSize: '16px', color: '#333', fontWeight: '500' }}>
                                                {policy.coverage_type || "N/A"}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="col-12 col-md-6 mb-4">
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={{ display: 'block', fontWeight: '600', color: '#666', marginBottom: '8px', fontSize: '13px', textTransform: 'uppercase' }}>
                                                Premium Amount
                                            </label>
                                            <p style={{ margin: 0, fontSize: '20px', color: 'var(--themecolor)', fontWeight: '600' }}>
                                                {formatCurrency(policy.premium_amount)}
                                            </p>
                                        </div>
                                    </div>
                                    {policy.start_date && (
                                        <div className="col-12 col-md-6 mb-4">
                                            <div style={{ marginBottom: '20px' }}>
                                                <label style={{ display: 'block', fontWeight: '600', color: '#666', marginBottom: '8px', fontSize: '13px', textTransform: 'uppercase' }}>
                                                    Start Date
                                                </label>
                                                <p style={{ margin: 0, fontSize: '16px', color: '#333', fontWeight: '500' }}>
                                                    {new Date(policy.start_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                    {policy.end_date && (
                                        <div className="col-12 col-md-6 mb-4">
                                            <div style={{ marginBottom: '20px' }}>
                                                <label style={{ display: 'block', fontWeight: '600', color: '#666', marginBottom: '8px', fontSize: '13px', textTransform: 'uppercase' }}>
                                                    End Date
                                                </label>
                                                <p style={{ margin: 0, fontSize: '16px', color: '#333', fontWeight: '500' }}>
                                                    {new Date(policy.end_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                    {policy.created_at && (
                                        <div className="col-12 col-md-6 mb-4">
                                            <div style={{ marginBottom: '20px' }}>
                                                <label style={{ display: 'block', fontWeight: '600', color: '#666', marginBottom: '8px', fontSize: '13px', textTransform: 'uppercase' }}>
                                                    Created Date
                                                </label>
                                                <p style={{ margin: 0, fontSize: '16px', color: '#333', fontWeight: '500' }}>
                                                    {new Date(policy.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div style={{ marginTop: '30px', paddingTop: '30px', borderTop: '1px solid #ddd' }}>
                                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                        <Button 
                                            type="primary"
                                            onClick={() => navigate(`/insurance/edit-policy/${policy.id}`)}
                                            style={{ height: '40px', borderRadius: '5px' }}
                                        >
                                            <EditOutlined /> Edit Policy
                                        </Button>
                                        <Button 
                                            danger
                                            onClick={() => {
                                                // Handle delete
                                                if (window.confirm('Are you sure you want to delete this policy?')) {
                                                    // Add delete logic here
                                                }
                                            }}
                                            style={{ height: '40px', borderRadius: '5px' }}
                                        >
                                            <DeleteOutlined /> Delete Policy
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="card" style={{ borderRadius: '5px' }}>
                            <div className="card-body" style={{ padding: '24px 30px', borderRadius: '5px' }}>
                                <div className="dc-emptydata-holder">
                                    <div className="dc-emptydata">
                                        <div className="dc-emptydetails dc-empty-booking dc-emptyholder-sm">
                                            <span></span>
                                            <em>Policy not found</em>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PolicyDetails;

